/** 1.4 P24/P25: one import's progress and rows, with invite resend. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Stack } from 'expo-router';

import { useOwnerScope } from '../context/OwnerScopeContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../components/Toast';
import ContentContainer from '../components/ContentContainer';
import Card from '../components/Card';
import Button from '../components/Button';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';

interface Imp { id: string; status: string; filename: string | null; rowCount: number; okCount: number; warningCount: number; errorCount: number; createdCount: number; linkedCount: number; failedCount: number; billingMode: string | null; sendInvites: boolean; createdAt: string; finishedAt: string | null; error: string | null }
interface Row { id: string; rowNumber: number; email: string | null; firstName: string | null; lastName: string | null; planName: string | null; status: string; messages: { code: string; message: string }[]; userId: string | null; subscriptionId: string | null; inviteId: string | null; inviteAcceptedAt?: string | null; inviteSentAt?: string | null }

const FILTERS = ['all', 'ok', 'warning', 'error', 'created', 'linked', 'failed'] as const;

export default function CustomerImportDetailScreen({ importId }: { importId: string }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const toast = useToast();
  const scope = useOwnerScope();
  const [imp, setImp] = useState<Imp | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ invited: number; accepted: number; reminded: number; cardAdded: number; pendingCard: number; suspended: number; memberships: number } | null>(null); // 1.4 P26

  const load = useCallback(async () => {
    try {
      const r = await scope.api.get<{ import: Imp; rows: Row[]; total: number }>(`/api/owner/imports/${importId}?page=${page}&limit=100${filter === 'all' ? '' : `&status=${filter}`}`);
      setImp(r.import); setRows(r.rows); setTotal(r.total);
      if (r.import.status === 'done') scope.api.get<typeof progress>(`/api/owner/imports/${importId}/progress`).then(setProgress).catch(() => null);
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not load the import', message: err.message }); }
    finally { setLoading(false); }
  }, [scope.api, importId, page, filter, toast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (imp?.status !== 'committing') return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [imp?.status, load]);

  const resend = async (rowId: string | 'all') => {
    setBusy(rowId);
    try {
      const r = await scope.api.post<{ sent: number }>(rowId === 'all' ? `/api/owner/imports/${importId}/resend-invites` : `/api/owner/imports/${importId}/rows/${rowId}/resend`, {});
      toast.show({ type: 'success', title: rowId === 'all' ? `Resent ${r.sent} invite${r.sent === 1 ? '' : 's'}` : 'Invite resent' });
      load();
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not resend', message: err.message }); }
    finally { setBusy(null); }
  };

  if (loading) return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  if (!imp) return null;
  const pages = Math.max(1, Math.ceil(total / 100));
  return (
    <>
      <Stack.Screen options={{ title: 'Customer Import' }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={960}>
          <Card style={styles.card}>
            <Text style={styles.title}>{imp.filename || 'Import'} · {new Date(imp.createdAt).toLocaleString()}</Text>
            <Text style={[styles.status, imp.status === 'done' && { color: colors.success }, imp.status === 'failed' && { color: colors.danger }]}>
              {imp.status === 'validated' ? 'Checked, not imported yet' : imp.status === 'committing' ? 'Importing…' : imp.status === 'done' ? 'Done' : imp.status}
            </Text>
            <Text style={styles.meta}>{imp.rowCount} rows · {imp.okCount} ready · {imp.warningCount} warnings · {imp.errorCount} errors{imp.status !== 'validated' ? ` · ${imp.createdCount} created · ${imp.linkedCount} linked · ${imp.failedCount} failed` : ''}{imp.billingMode ? ` · ${imp.billingMode === 'active_grace' ? 'active with grace period' : 'pending setup'}` : ''}</Text>
            {!!imp.error && <Text style={styles.err}>{imp.error}</Text>}
            {imp.status === 'done' && progress && (
              <View style={styles.progressRow}>
                {([['Invited', progress.invited], ['Accepted', progress.accepted], ['Card added', progress.cardAdded], ['Awaiting card', progress.pendingCard], ['Paused', progress.suspended]] as const).map(([label, n]) => (
                  <View key={label} style={styles.progressCell}><Text style={styles.progressNum}>{n}</Text><Text style={styles.progressLabel}>{label}</Text></View>
                ))}
              </View>
            )}
            {imp.status === 'done' && (
              <Button title="Resend invites to everyone who has not accepted" variant="outline" size="sm" onPress={() => resend('all')} loading={busy === 'all'} style={{ alignSelf: 'flex-start', marginTop: spacing.sm }} />
            )}
          </Card>
          <Card style={styles.card}>
            <View style={styles.filters}>
              {FILTERS.map(f => (
                <TouchableOpacity key={f} style={[styles.chip, filter === f && styles.chipActive]} onPress={() => { setFilter(f); setPage(1); }}>
                  <Text style={[styles.chipText, filter === f && { color: colors.primary }]}>{f}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {rows.length === 0 && <Text style={styles.meta}>No rows.</Text>}
            {rows.map(r => (
              <View key={r.id} style={styles.row}>
                <Text style={styles.num}>{r.rowNumber}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={styles.email} numberOfLines={1}>{r.email ?? '—'} · {[r.firstName, r.lastName].filter(Boolean).join(' ')}</Text>
                  <Text style={styles.meta} numberOfLines={2}>{r.planName ?? ''}{r.messages.length ? ` · ${r.messages.map(m => m.message).join(' · ')}` : ''}{r.inviteAcceptedAt ? ' · invite accepted' : r.inviteSentAt ? ' · invite sent' : ''}</Text>
                </View>
                <Text style={[styles.rowStatus, (r.status === 'error' || r.status === 'failed') && { color: colors.danger }, (r.status === 'created' || r.status === 'linked') && { color: colors.success }]}>{r.status}</Text>
                {(r.status === 'created' || r.status === 'linked') && r.inviteId && !r.inviteAcceptedAt && (
                  <TouchableOpacity onPress={() => resend(r.id)} disabled={busy === r.id}><Text style={styles.link}>{busy === r.id ? '…' : 'Resend'}</Text></TouchableOpacity>
                )}
              </View>
            ))}
            {pages > 1 && (
              <View style={styles.pager}>
                <Button title="Prev" size="sm" variant="outline" disabled={page <= 1} onPress={() => setPage(page - 1)} />
                <Text style={styles.meta}>Page {page} of {pages}</Text>
                <Button title="Next" size="sm" variant="outline" disabled={page >= pages} onPress={() => setPage(page + 1)} />
              </View>
            )}
          </Card>
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  card: { marginBottom: spacing.md },
  title: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  status: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: 2 },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  err: { fontSize: fontSize.sm, color: c.danger, marginTop: spacing.xs },
  filters: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: 12, borderWidth: 1, borderColor: c.border },
  chipActive: { borderColor: c.primary, backgroundColor: c.primary + '12' },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.semibold },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  num: { width: 36, fontSize: fontSize.xs, color: c.textSecondary },
  email: { fontSize: fontSize.sm, color: c.text },
  rowStatus: { width: 64, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textAlign: 'right' },
  link: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.semibold },
  pager: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.sm },
  progressRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm },
  progressCell: { minWidth: 84, padding: spacing.sm, borderRadius: borderRadius.md, backgroundColor: c.surfaceSecondary, alignItems: 'center' },
  progressNum: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  progressLabel: { fontSize: 10, color: c.textSecondary },
});
